// Completing a finding.
//
// A check knows what it found and where. It should not also have to know how
// the finding is titled for a non-specialist, which part of the plan it is
// grouped under, or how it is phrased as a review comment — that is the same
// decision fifteen times over, and fifteen checks making it separately is how
// two findings of the same kind end up worded differently.
//
// So checks emit a FlagDraft and this fills in the rest, once, from the
// taxonomy. The one thing not derived here is quoteOffsets: only the check that
// located the span knows where in the document it was looking.

import { getFailureMode } from '../data/taxonomy';
import type { Flag, FlagDraft } from '../types';
import { commentDraftFor } from './comment/commentDraft';

/**
 * The first sentence of the taxonomy's plain description, used as the line of
 * context under a finding's claim where the mode carries no plainContext of
 * its own. Falls back to the whole description rather than to nothing, because
 * a slightly long context line is better than a missing one.
 */
export function firstSentence(text: string): string {
  const trimmed = text.trim();
  const match = /^.*?[.!?](?=\s|$)/.exec(trimmed);
  if (!match) return trimmed;
  return match[0].length >= 20 ? match[0] : trimmed;
}

export function enrichFlag(draft: FlagDraft): Flag {
  const mode = getFailureMode(draft.failureModeId);
  const { claim, contextLine, ...rest } = draft;

  return {
    ...rest,
    // A hand-transcribed or previously stored finding may predate the field.
    // null is the honest value: the pane says it cannot show the span.
    quoteOffsets: rest.quoteOffsets ?? null,
    planArea: mode.planArea,
    claim: claim?.trim() || mode.plainTitle,
    contextLine:
      contextLine?.trim() ||
      mode.plainContext ||
      firstSentence(mode.plain_description),
    commentDraft: commentDraftFor({
      failureModeId: draft.failureModeId,
      sectionRef: draft.sectionRef,
      page: draft.page,
      quote: draft.quote,
      receipt: draft.receipt,
    }),
  };
}
